import type { ReactNode } from 'react';
import React from 'react';

import { Layout } from 'antd';
import { Link } from 'react-router';

import { formatRoute } from '/imports/modules/Routing';
import { loginLink } from '/imports/startup/client/routes';
import { useGlobalContext } from '/imports/ui/components/GlobalContext';
import type { ItemType } from '/imports/ui/components/Menu';
import { ThemeSwitch } from '/imports/ui/components/ThemeSwitch';
import { TopNavApp } from '/imports/ui/components/TopNav';
import { styled } from '/imports/ui/foundation/Theme';
import { getUserMenu } from '/imports/ui/layouts/getUserMenu';
import { adminRoute, adminSlug } from '/imports/ui/pages/admin/routes';
import { enroledRoute, enroledSlug } from '/imports/ui/pages/enrolled/routes';
import { myCoursesRoute, mycoursesSlug } from '/imports/ui/pages/myCourses/routes';
import { myExamsRoute, myexamsSlug } from '/imports/ui/pages/myExams/routes';
import { myGroupsRoute, mygroupsSlug } from '/imports/ui/pages/myGroups/routes';
import { printSlug } from '/imports/ui/pages/print/routes';

const StyledLayoutHeader = styled(Layout.Header, ({ tokens }) => ({
  '@media print': {
    display: 'none',
  },
  '&.ant-layout-header': {
    boxSizing: 'content-box',
  },
  padding: '0 !important',
  variants: {
    headerColor: {
      Black: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderBlack}` },
      Red: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderRed}` },
      Gold: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderGold}` },
      Green: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderGreen}` },
      Blue: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderBlue}` },
      Magenta: { boxShadow: `inset 0 -2px 0 0 ${tokens.colorHeaderMagenta}` },
    },
  },
}));

const _topNavKeys = {
  none: null,
  app: null,
  [enroledSlug]: null,
  [mygroupsSlug]: null,
  [mycoursesSlug]: null,
  [myexamsSlug]: null,
  [adminSlug]: null,
  [printSlug]: null,
  login: null,
  cxUserMenu: null,
  themeSwitch: null,
};

export type TopNavKey = keyof typeof _topNavKeys;

export const TopNavLayoutWrapper = ({
  menu,
  children,
}: React.PropsWithChildren<{
  menu: ReactNode;
}>) => {
  const { headerColor } = useGlobalContext();
  return (
    <Layout style={{ minHeight: '100%' }}>
      <StyledLayoutHeader headerColor={headerColor}>{menu}</StyledLayoutHeader>
      {children}
    </Layout>
  );
};

export function TopNavLayout({
  selected,
  disableLogin,
  children,
}: React.PropsWithChildren<{ disableLogin?: boolean; selected: TopNavKey | undefined }>) {
  const { user, userCapabilities, examMode } = useGlobalContext();

  const userItem: ItemType<'cxUserMenu' | 'login'> =
    user != null
      ? getUserMenu(user)
      : !disableLogin
        ? { key: 'login', label: <Link to={loginLink}>Login</Link> }
        : null;

  return (
    <TopNavLayoutWrapper
      menu={
        <TopNavApp<TopNavKey>
          menu={[
            userCapabilities.includes('enrolled')
              ? {
                  key: 'enrolled',
                  label: <Link to={formatRoute(enroledRoute)({})}>Courses</Link>,
                }
              : null,
            userCapabilities.includes('groupAdmin')
              ? {
                  key: 'mygroups',
                  label: <Link to={formatRoute(myGroupsRoute)({})}>Exercise groups</Link>,
                }
              : null,
            userCapabilities.includes('courseAdmin')
              ? {
                  key: 'mycourses',
                  label: <Link to={formatRoute(myCoursesRoute)({})}>Course management</Link>,
                }
              : null,
            examMode && userCapabilities.includes('courseAdmin')
              ? {
                  key: 'myexams',
                  label: <Link to={formatRoute(myExamsRoute)({})}>Exams</Link>,
                }
              : null,
            userCapabilities.includes('admin')
              ? {
                  key: 'admin',
                  label: <Link to={formatRoute(adminRoute)({})}>Admin</Link>,
                }
              : null,
            { type: 'divider' },
            ...(userItem != null ? [userItem] : []),
            {
              key: 'themeSwitch',
              label: <ThemeSwitch />,
            },
          ]}
          selected={selected}
        />
      }
    >
      {children}
    </TopNavLayoutWrapper>
  );
}
